// Formatting in Italian, compact and aligned for dense monitoring screens.

import type { ApplicationKind, CheckKind, UsageMetric } from '@monitor/contracts';

/** Visible names of the usage metrics: every label starts with a capital letter. */
export const USAGE_LABEL: Record<UsageMetric, string> = {
  request: 'Richieste API',
  token: 'Token',
  bandwidth: 'Bandwidth',
  storage: 'Storage',
  database: 'Database',
  queue: 'Queue',
  message: 'Messaggi',
  operation: 'Operazioni',
};

/** Visible names of the Application kinds. */
export const APP_KIND_LABEL: Record<ApplicationKind, string> = { web: 'Web', api: 'API', worker: 'Worker', job: 'Job', gateway: 'Gateway' };

/** Visible names of the health check kinds. */
export const CHECK_LABEL: Record<CheckKind, string> = { endpoint: 'Endpoint', database: 'Database', queue: 'Queue', dependency: 'Dipendenza' };

const eur = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const eurCompact = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const integer = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 });
const compact = new Intl.NumberFormat('it-IT', { notation: 'compact', maximumFractionDigits: 1 });

export const money = (value: number) => eur.format(value);
export const moneyShort = (value: number) => (Math.abs(value) >= 100 ? eurCompact.format(value) : eur.format(value));
export const int = (value: number) => integer.format(Math.round(value));
export const short = (value: number) => (Math.abs(value) < 10_000 ? integer.format(Math.round(value)) : compact.format(value));
/** Percentage; null (a metric the platform does not expose) shows a dash. */
export const pct = (ratio: number | null, digits = 1) => (ratio === null ? '' : `${(ratio * 100).toFixed(digits).replace('.', ',')}%`);
export const ms = (value: number) => (value >= 10_000 ? `${(value / 1000).toFixed(1).replace('.', ',')} s` : `${int(value)} ms`);

/** Dimensione in byte leggibile: 812 KB, 21,6 MB, 1,41 GB. */
export function bytes(value: number) {
  const step = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = Math.abs(value);
  let index = 0;
  while (size >= 1024 && index < step.length - 1) { size /= 1024; index += 1; }
  const text = index === 0 ? String(Math.round(size)) : size.toFixed(size < 10 ? 2 : size < 100 ? 1 : 0).replace('.', ',');
  return `${value < 0 ? '−' : ''}${text} ${step[index]}`;
}

export function unit(value: number, unitName: string) {
  if (unitName === 'GB') return `${value.toFixed(value < 10 ? 2 : 0).replace('.', ',')} GB`;
  if (unitName === 'token') return `${short(value)} token`;
  return `${short(value)} ${unitName}`;
}

/** Relative time: "adesso", "3 min fa", "2 h fa", "3 g fa", "12 set". */
export function ago(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '';
  const diff = now - Date.parse(iso);
  if (diff < 45_000) return 'adesso';
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)} min fa`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)} h fa`;
  if (diff < 7 * 86_400_000) return `${Math.round(diff / 86_400_000)} g fa`;
  return new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
}

/** A fragment shown on its own (a cell, a foot, a tooltip title) starts with a capital letter. */
export const capital = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** Relative time shown on its own (a cell, a foot): the fragment starts with a capital letter. */
export function agoLabel(iso: string | null | undefined, now = Date.now()): string {
  return capital(ago(iso, now));
}

export const time = (iso: string) => new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
export const timeShort = (iso: string) => new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
export const dateTime = (iso: string) => new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
export const day = (key: string) => new Date(`${key}T12:00:00Z`).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
/** "Lun 12 set": tooltip title of a daily chart, on its own. */
export const weekday = (key: string) => capital(new Date(`${key}T12:00:00Z`).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' }));
/** "Lun 12": axis tick of a chart over a week. */
export const weekdayShort = (iso: string) => capital(new Date(iso).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric' }));

/** Change ratio between two values; null when there is no previous value. */
export const change = (current: number, previous: number) => (previous > 0 ? current / previous - 1 : null);

export function initials(name: string) {
  return name
    .replace(/[^\p{L}\s]/gu, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
}
